import React from 'react';
import EntryData from './EntryData';

/**
 * ReviewDataItem: Similar to DataManagementItem — actions in the header and
 * EntryData rendering for the body. Uses the shared data-item styles so both
 * pages look/behave consistently.
 *
 * Props:
 * - row: the review row object from the review query
 * - entriesById: map/dictionary of entry_id -> full entry (optional)
 * - unifiedRawKeys: array of raw keys to render (optional)
 * - unifiedOutputKeys: array of output keys to render (optional)
 * - actions: object containing callbacks: { approve, deny, markUnreviewed, delete }
 */
function ReviewDataItem({ row, entriesById = {}, unifiedRawKeys, unifiedOutputKeys, actions, fileDownloadContext }) {
  if (!row) return null;

  const isAccepted = row.reviewed && row.approved;
  const isDenied = row.reviewed && !row.approved;
  const isPending = !row.reviewed;

  // Prefer full entry details if available, otherwise compose from row metadata/output
  // 1) entriesById: fetched from /entries/query by id (authoritative)
  //    - Resolve using multiple possible keys and normalize string/number ids
  // 2) row.entry or row.data: some backends include the full entry directly on the review row
  const getFromEntriesById = () => {
    const candidates = [
      row && row.entry_id,
      row && row.entryId,
      row && row.id, // in some implementations, review id matches entry id
      row && row.entry && row.entry.id,
      row && row.data && row.data.id,
    ].filter((v) => v !== undefined && v !== null);
    const tryKeys = [];
    for (const k of candidates) {
      tryKeys.push(k);
      // Normalize to string/number variants to avoid type mismatches in object keys
      const asNum = typeof k === 'string' && k.trim() !== '' && !Number.isNaN(Number(k)) ? Number(k) : null;
      const asStr = typeof k === 'number' ? String(k) : (typeof k === 'string' ? k : null);
      if (asNum !== null) tryKeys.push(asNum);
      if (asStr !== null) tryKeys.push(asStr);
    }
    for (const key of tryKeys) {
      if (Object.prototype.hasOwnProperty.call(entriesById, key)) {
        return entriesById[key];
      }
    }
    return undefined;
  };
  const fromEntries = getFromEntriesById();

  // Embedded entry (if provided by backend on the review row)
  // Support multiple possible shapes: row.entry (full entry), row.data (full entry),
  // or row.raw + row.output (raw data with optional outputs on the review row itself).
  const embedded = (row && typeof row.entry === 'object' && row.entry !== null)
    ? row.entry
    : (row && typeof row.data === 'object' && row.data !== null)
      ? row.data
      : (row && typeof row.raw === 'object' && row.raw !== null)
        ? { raw: row.raw, ...(row && typeof row.output === 'object' && row.output !== null ? { output: row.output } : {}) }
        : null;
  // Normalize embedded object into an entry shape with `raw` when needed
  const fromRowEmbedded = (() => {
    if (!embedded) return null;
    const hasRaw = embedded && typeof embedded.raw === 'object' && embedded.raw !== null;
    if (hasRaw) return embedded;
    // If there's an `output` alongside raw-ish fields, preserve it
    const maybeOutput = embedded && typeof embedded.output === 'object' && embedded.output !== null ? embedded.output : undefined;
    return maybeOutput ? { raw: embedded, output: maybeOutput } : { raw: embedded };
  })();
  // Build a safe raw fallback from metadata; strip identifiers like id/entry_id so they don't render as fields
  const rawFallback = (() => {
    const meta = row && row.metadata && typeof row.metadata === 'object' ? { ...row.metadata } : {};
    if (meta && typeof meta === 'object') {
      delete meta.id;
      delete meta.entry_id;
    }
    return meta;
  })();
  const outputFallback = row && typeof row.output === 'object' && row.output !== null ? row.output : undefined;
  // Always pass an entry-shaped object (with a `raw` property) so RawEntryFields
  // behaves identically to the Data Management page when deriving visible fields.
  const composedEntry = fromEntries
    || fromRowEmbedded
    || (outputFallback ? { raw: rawFallback, output: outputFallback } : { raw: rawFallback });
  const entryForDisplay =
    composedEntry && composedEntry.id == null && row?.entry_id != null
      ? { ...composedEntry, id: row.entry_id }
      : composedEntry;

  return (
    <div className={`data-item ${isAccepted ? 'row-accepted' : ''} ${isDenied ? 'row-denied' : ''}`}>
      <div className="data-item-header">
        <div className="data-item-number">
          {/* Status badges in place of number for review items */}
          {isPending && <span className="status-badge status-pending">Pending</span>}
          {isAccepted && <span className="status-badge status-accepted">Accepted</span>}
          {isDenied && <span className="status-badge status-denied">Denied</span>}
        </div>
        <div className="data-item-actions">
          {isPending ? (
            <>
              <button className="btn btn-success btn-sm" onClick={actions?.approve}>
                <i className="fas fa-check"></i> Approve
              </button>
              <button className="btn btn-danger btn-sm" onClick={actions?.deny}>
                <i className="fas fa-times"></i> Deny
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-warning btn-sm" onClick={actions?.markUnreviewed}>
                <i className="fas fa-undo"></i> Mark Unreviewed
              </button>
              <button className="btn btn-outline-danger btn-sm" onClick={actions?.delete}>
                <i className="fas fa-trash"></i> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Data Display */}
      <EntryData
        entry={entryForDisplay}
        // Show outputs like Data Management by default
        // Ensure the exact same classes used in Data Management
        fieldsClass="data-item-fields"
        fieldClass="data-field"
        outputsFieldsClass="data-item-fields"
        outputFieldClass="data-field"
        unifiedRawKeys={unifiedRawKeys}
        unifiedOutputKeys={unifiedOutputKeys}
        fileDownloadContext={fileDownloadContext}
        // Use the same field classes as Data Management for consistency
        // (fall back to defaults used by RawEntryFields if not provided)
      />

      {/* Meta */}
      <div className="row-meta">
        <span>
          Created: {row.created_at ? new Date(row.created_at).toLocaleString() : 'n/a'}
        </span>
        {row.modified_at && (
          <span>Modified: {new Date(row.modified_at).toLocaleString()}</span>
        )}
        {row.reviewed_at && (
          <span>Reviewed: {new Date(row.reviewed_at).toLocaleString()}</span>
        )}
      </div>

      {row.reasoning && (
        <div className="row-reasoning">
          <strong>Reasoning:</strong>
          <div className="row-reasoning-text">{row.reasoning}</div>
        </div>
      )}
    </div>
  );
}

export default ReviewDataItem;
